import React from 'react';
import { KeyRound, CheckCircle, Loader2, Check, Copy } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';

/**
 * @typedef {Object} ResetPwFormState
 * @property {'generate'|'manual'} mode
 * @property {string} password
 *
 * @typedef {Object} ResetPasswordDialogProps
 * @property {boolean} open
 * @property {(open: boolean) => void} onOpenChange
 * @property {{name?: string, email?: string} | null} selectedUser
 * @property {ResetPwFormState} form
 * @property {(updater: (prev: ResetPwFormState) => ResetPwFormState) => void} setForm
 * @property {() => Promise<void>} onReset
 * @property {boolean} saving
 * @property {string} generatedPassword
 * @property {(pw: string) => void} setGeneratedPassword
 * @property {boolean} copied
 * @property {(v: boolean) => void} setCopied
 */

/**
 * Password reset modal — super admin can generate a strong password or
 * define one manually. On success shows the generated password once with
 * copy-to-clipboard, in case the email delivery fails.
 *
 * @param {ResetPasswordDialogProps} props
 */
export const ResetPasswordDialog = ({
  open, onOpenChange, selectedUser,
  form, setForm, onReset, saving,
  generatedPassword, setGeneratedPassword,
  copied, setCopied,
}) => (
  <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) { setGeneratedPassword(''); } }}>
    <DialogContent className="bg-slate-50 border-slate-200 text-slate-900" data-testid="reset-pw-dialog">
      <DialogHeader>
        <DialogTitle className="text-slate-900 flex items-center gap-2">
          <KeyRound className="w-5 h-5 text-amber-600" /> Repor Password
        </DialogTitle>
        <DialogDescription className="text-slate-500">
          Definir nova password para <strong className="text-slate-900">{selectedUser?.name}</strong>{' '}
          ({selectedUser?.email}). A nova password é enviada por email ao utilizador.
        </DialogDescription>
      </DialogHeader>

      {generatedPassword ? (
        <div className="space-y-3">
          <Alert className="bg-green-50 border-green-200">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <AlertDescription className="text-green-700">
              Password reposta e enviada por email. Guarde-a caso o email falhe:
            </AlertDescription>
          </Alert>
          <div className="flex items-center gap-2 p-3 bg-white rounded-lg border border-slate-200">
            <code className="text-indigo-700 text-sm break-all flex-1" data-testid="generated-password">{generatedPassword}</code>
            <Button
              size="sm" variant="outline" className="border-slate-300 text-slate-700"
              onClick={() => {
                navigator.clipboard.writeText(generatedPassword);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              }}
              data-testid="copy-generated-pw-btn"
            >
              {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex gap-2">
            <Button
              type="button" variant={form.mode === 'generate' ? 'default' : 'outline'}
              onClick={() => setForm(prev => ({ ...prev, mode: 'generate' }))}
              className={form.mode === 'generate' ? 'flex-1 bg-indigo-700 text-white' : 'flex-1 border-slate-300 text-slate-600'}
              data-testid="pw-mode-generate"
            >
              Gerar automática
            </Button>
            <Button
              type="button" variant={form.mode === 'manual' ? 'default' : 'outline'}
              onClick={() => setForm(prev => ({ ...prev, mode: 'manual' }))}
              className={form.mode === 'manual' ? 'flex-1 bg-indigo-700 text-white' : 'flex-1 border-slate-300 text-slate-600'}
              data-testid="pw-mode-manual"
            >
              Definir manualmente
            </Button>
          </div>
          {form.mode === 'manual' ? (
            <div>
              <Label className="text-slate-600">Nova password (mín. 8 caracteres)</Label>
              <Input
                type="text" value={form.password}
                onChange={(e) => setForm(prev => ({ ...prev, password: e.target.value }))}
                placeholder="Nova password"
                className="bg-white border-slate-300 text-slate-900 font-mono"
                data-testid="manual-pw-input"
              />
            </div>
          ) : (
            <p className="text-sm text-slate-500">
              Será gerada uma password forte de 16 caracteres e enviada por email ao utilizador.
            </p>
          )}
        </div>
      )}

      <DialogFooter>
        {generatedPassword ? (
          <Button
            onClick={() => { onOpenChange(false); setGeneratedPassword(''); }}
            className="bg-indigo-700 hover:bg-amber-600 text-black"
            data-testid="close-reset-pw-btn"
          >
            Concluído
          </Button>
        ) : (
          <>
            <Button variant="outline" onClick={() => onOpenChange(false)} className="border-slate-300 text-slate-600 hover:bg-slate-100">
              Cancelar
            </Button>
            <Button
              onClick={onReset}
              disabled={saving || (form.mode === 'manual' && form.password.length < 8)}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="confirm-reset-pw-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
              Repor e enviar
            </Button>
          </>
        )}
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
